import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@simplicity/ui";
import { useState } from "react";
import { useNavigate } from "react-router";

interface CreateSparkDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CreateResponse {
  id: string;
  title: string;
  slug: string;
}

async function createSparkDashboard(
  tenantId: string,
  payload: { title: string },
): Promise<CreateResponse> {
  return portalFetch<CreateResponse>(`/api/bi/tenants/${tenantId}/dashboards`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tenantId,
      title: payload.title.trim(),
      definition: { schemaVersion: 2, datasets: [], components: [] },
    }),
  });
}

export function CreateSparkDashboardDialog({
  open,
  onOpenChange,
}: CreateSparkDashboardDialogProps) {
  const navigate = useNavigate();
  const { tenantPath, currentTenantId } = useTenant();

  const [title, setTitle] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SIM-2336 — Category + School Year were removed from creation: they are no
  // longer fixed dashboard attributes (the bi `create` contract treats both as
  // optional), and their dropdowns were fed from now-deprecated PropertyDef
  // options, so the old required gate could never be satisfied.
  const canSubmit = title.trim().length > 0;

  const handleCreate = async () => {
    if (!currentTenantId || !canSubmit) return;
    setIsCreating(true);
    setError(null);
    try {
      const result = await createSparkDashboard(currentTenantId, { title });
      onOpenChange(false);
      void navigate(tenantPath(`/spark/${result.id}?edit=1`));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create dashboard");
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setTitle("");
      setError(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New SimplySpark Dashboard</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="spark-title" className="text-xs">
              Title
            </Label>
            <Input
              id="spark-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Dashboard title"
              autoFocus
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={() => void handleCreate()} disabled={!canSubmit || isCreating}>
            {isCreating ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
